import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle, XCircle, SealCheck, Prohibit, WarningCircle, Eye } from "@phosphor-icons/react";
import { QuoteNarrative } from "../components/portal/QuoteNarrative";
import { brandVars } from "../lib/brandTheme";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const PLATFORM_MARK = "/icon-192x192.png";
const brandLogo = (url) => (url ? (url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : PLATFORM_MARK);
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Shell({ brand, preview, children }) {
  const brandName = brand.company_name || "Fixlr";
  return (
    <div className={`${preview ? "min-h-0 rounded-lg" : "min-h-screen"} bg-slate-100 py-8 px-4 relative`} style={brandVars(brand.primary_color)} data-testid={preview ? "quote-view-preview" : "quote-view-page"}>
      {preview && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1" data-testid="quote-view-preview-ribbon">
          <Eye size={12} weight="bold" /> Preview
        </div>
      )}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <img src={brandLogo(brand.logo_url)} alt={brandName} className="h-14 mx-auto object-contain" data-testid="quote-view-logo" onError={(e) => { if (!e.currentTarget.src.endsWith(PLATFORM_MARK)) e.currentTarget.src = PLATFORM_MARK; }} />
          <p className="mt-2 text-sm font-semibold text-slate-700" data-testid="quote-view-brand-name">{brandName}</p>
        </div>
        {children}
        <p className="text-center text-[11px] text-slate-400 mt-6" data-testid="quote-view-footer">
          {brand.footer_text || brand.tagline || brandName}
        </p>
      </div>
    </div>
  );
}

/* The client-facing quote page. Rendered live at /quote/:token and, with `preview`, inside the admin Brand Preview dialog. */
export function QuoteApprovalView({ quote, error, done, submitting, onRespond, preview = false }) {
  const [mode, setMode] = useState(null); // null | "approve" | "decline"
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const brand = quote?.branding || {};

  if (error) return (
    <Shell brand={brand} preview={preview}>
      <div className="bg-white rounded-lg shadow p-10 text-center" data-testid="quote-view-error">
        <WarningCircle size={44} className="mx-auto text-amber-500 mb-3" />
        <h2 className="text-lg font-semibold text-slate-800">Link unavailable</h2>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
      </div>
    </Shell>
  );
  if (!quote) return (
    <Shell brand={brand} preview={preview}><div className="bg-white rounded-lg shadow p-10 text-center text-sm text-slate-500" data-testid="quote-view-loading">Loading your quote…</div></Shell>
  );

  const responded = done || (quote.client_response ? quote.client_response.decision : null)
    || (["approved", "declined", "converted"].includes(quote.status) ? quote.status : null);

  if (responded) {
    const ok = responded === "approved" || responded === "converted";
    return (
      <Shell brand={brand} preview={preview}>
        <div className="bg-white rounded-lg shadow p-10 text-center" data-testid="quote-view-responded">
          {ok ? <SealCheck size={52} className="mx-auto text-emerald-500 mb-3" weight="fill" />
              : <Prohibit size={52} className="mx-auto text-red-400 mb-3" />}
          <h2 className="text-xl font-bold text-slate-800">
            {ok ? "Quote approved — thank you!" : "Quote declined"}
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            {ok ? `Quote ${quote.reference} has been approved${done ? "" : " already"}. We'll be in touch shortly to get the work scheduled.`
                : `Quote ${quote.reference} has been declined. If you'd like to discuss changes, just reply to our email — we're happy to revise it.`}
          </p>
          {quote.client_response?.name && !done && (
            <p className="text-xs text-slate-400 mt-3">Signed by {quote.client_response.name} on {(quote.client_response.at || "").slice(0, 10)}</p>
          )}
        </div>
      </Shell>
    );
  }

  const addr = [quote.property_name, quote.street_address, quote.town_city, quote.county, quote.postcode].filter(Boolean).join(", ");
  const confirm = () => {
    if (preview) { toast.info("Preview only — nothing is sent from here"); return; }
    if (name.trim().length < 2) { toast.error("Please type your full name to sign"); return; }
    onRespond(mode === "approve" ? "approved" : "declined", name.trim(), comment.trim());
  };

  return (
    <Shell brand={brand} preview={preview}>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="quote-view-card">
        <div className="bg-slate-900 text-white px-6 py-5" style={brand.primary_color ? { backgroundColor: brand.primary_color } : undefined} data-testid="quote-view-header">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-white/70">Quotation</p>
              <h1 className="text-xl font-bold mt-0.5" data-testid="quote-view-ref">{quote.reference}</h1>
              {quote.title && <p className="text-sm text-white/80 mt-0.5">{quote.title}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-widest text-white/70">Total {quote.vat_enabled ? "inc. VAT" : ""}</p>
              <p className="text-2xl font-bold" data-testid="quote-view-total">{gbp(quote.total_inc_vat)}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-slate-100 grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-400">Prepared for</p>
            <p className="font-medium text-slate-800">{quote.client_name || "—"}</p>
            {quote.client_company && <p className="text-slate-500 text-xs">{quote.client_company}</p>}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-slate-400">Property</p>
            <p className="text-slate-700 text-xs leading-relaxed">{addr || "—"}</p>
          </div>
        </div>

        <div className="px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 pr-3 text-center">Qty</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(quote.line_items || []).map((li, i) => (
                <tr key={i} className="border-b border-slate-100 align-top" data-testid={`quote-line-${i}`}>
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{li.item_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-500 text-xs whitespace-pre-line">{li.description}</td>
                  <td className="py-2.5 pr-3 text-center text-slate-600">{li.quantity}</td>
                  <td className="py-2.5 text-right font-medium text-slate-800">{gbp(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end mt-3">
            <div className="w-56 text-sm space-y-1.5">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{gbp(quote.subtotal)}</span></div>
              {quote.vat_enabled && (
                <div className="flex justify-between text-slate-500"><span>VAT ({quote.vat_rate}%)</span><span>{gbp(quote.vat_amount)}</span></div>
              )}
              <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1.5"><span>Total</span><span>{gbp(quote.total_inc_vat)}</span></div>
            </div>
          </div>
          <QuoteNarrative quote={quote} showTerms className="mt-4" />
          <p className="text-[11px] text-slate-400 mt-3">Quote valid for {quote.validity_days} days from issue.</p>
        </div>

        <div className="px-6 py-5 bg-slate-50 border-t border-slate-200">
          {!mode ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-base" onClick={() => setMode("approve")} data-testid="quote-approve-btn">
                <CheckCircle size={20} className="mr-2" weight="fill" /> Approve Quote
              </Button>
              <Button variant="outline" className="flex-1 text-red-600 border-red-200 hover:bg-red-50 h-11" onClick={() => setMode("decline")} data-testid="quote-decline-btn">
                <XCircle size={18} className="mr-2" /> Decline
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid={`quote-${mode}-form`}>
              <p className="text-sm font-semibold text-slate-800">
                {mode === "approve" ? "Approve this quote" : "Decline this quote"}
              </p>
              <div>
                <Label className="text-xs">Type your full name to sign *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" className="bg-white" data-testid="quote-sign-name" />
              </div>
              <div>
                <Label className="text-xs">{mode === "approve" ? "Comment (optional)" : "Reason (optional)"}</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="bg-white h-20 text-sm" data-testid="quote-sign-comment" />
              </div>
              <div className="flex gap-2">
                <Button
                  className={mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
                  disabled={submitting} onClick={confirm}
                  data-testid="quote-confirm-btn">
                  {submitting ? "Submitting…" : mode === "approve" ? "Confirm Approval" : "Confirm Decline"}
                </Button>
                <Button variant="ghost" onClick={() => setMode(null)} disabled={submitting} data-testid="quote-cancel-btn">Cancel</Button>
              </div>
              <p className="text-[10px] text-slate-400">
                By confirming you agree this acts as your electronic signature accepting {mode === "approve" ? "" : "or declining "}quote {quote.reference}.
              </p>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

export default function QuoteApprovalPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    API.get(`/public/quote-approval/${token}`)
      .then(({ data }) => setQuote(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or has expired."));
  }, [token]);

  const respond = async (decision, name, comment) => {
    setSubmitting(true);
    try {
      await API.post(`/public/quote-approval/${token}/respond`, { decision, name, comment });
      setDone(decision);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong — please try again");
    } finally { setSubmitting(false); }
  };

  return <QuoteApprovalView quote={quote} error={error} done={done} submitting={submitting} onRespond={respond} />;
}
